import React, {useCallback, useContext, useEffect, useState} from 'react';

import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContext} from 'src/contexts/validation-context';
import {BASIC_VALIDATION_FAILED} from 'src/modules/create-account/constants';

import {ContactInformation} from './components/contact-information';
import {UserAccountInformation} from './components/user-account-information';
import {UserCrmSettingsSection} from './components/user-crm-settings/user-crm-settings';
import {UserDetails} from './components/user-details';
import {useUserCrmSettings} from './useUserCrmSettings';
import {useUserDetailsValidation} from './useUserDetailsValidation';
import {useUserInformation} from './useUserInformation';

import type {SaveOptions, SaveWithReason} from 'src/components/save-with-reason/save-with-reason-button';
import type {MessageContextType} from 'src/contexts/message-context';
import type {ValidationContextType} from 'src/contexts/validation-context';
import type {Error} from 'src/openapi/admin/models/Error';

type UserDetailsContent = {
    isLoading: boolean,
    isChunkChanged: boolean,
    loadingError: Error | null,
    content: React.ReactNode,
    busy: boolean,
    onSave: SaveWithReason,
    onDiscard: () => void,
};

type Props = {
    userId?: number,
    accountId: number,
    onUserCreate?: (createdUserId: number) => void,
    onProceed: (existedUserId?: number) => void,
    children: (content: UserDetailsContent) => React.ReactElement,
    editDisabled?: boolean,
    onFieldChange: (section: string) => (fieldName: string) => void,
};

export const UserDetailsController = (props: Props) => {
    const {userId, accountId, onUserCreate, onProceed, children, editDisabled, onFieldChange} = props;
    const [busy, setBusy] = useState<boolean>(false);

    const {setErrorMessage} = useContext<MessageContextType>(MessageContext);
    const {validationErrors, clearErrors} = useContext<ValidationContextType>(ValidationContext);
    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'user-details',
        onProceed,
    });

    const {
        isLoading: isUserInformationLoading,
        loadingError: userInformationLoadingError,
        userInformation,
        availableStatuses,
        setUserInformation,
        processUser,
        saveSfdcId,
        isChanged: isChangedUserUpdateRequest,
        discardChanges,
    } = useUserInformation({userId, accountId});

    const {
        isLoading: isUserCrmSettingLoading,
        loadingError: userCrmSettingsLoadingError,
        isChanged: isUserCrmSettingsChanged,
        data: userCrmSettings,
        setData: setUserCrmSettings,
        saveChanges: saveUserCrmSettings,
        discardChanges: discardUserCrmSetting,
    } = useUserCrmSettings({userId: userId || 0, accountId});

    const {validateUnfilledFields} = useUserDetailsValidation();

    useEffect(() => {
        setIsChunkChanged(isChangedUserUpdateRequest || isUserCrmSettingsChanged);
    }, [isChangedUserUpdateRequest, isUserCrmSettingsChanged, setIsChunkChanged]);

    const onSave = useCallback(async (options?: SaveOptions) => {
        const hasValidationErrors = validateUnfilledFields(userInformation) || validationErrors.length;

        if (hasValidationErrors) {
            setErrorMessage(BASIC_VALIDATION_FAILED);
            return BASIC_VALIDATION_FAILED;
        }

        let saveError: string | undefined;
        setBusy(true);
        try {
            await processUser(async (processedUserId: number, processUserErrors: string[]) => {
                const updateUserErrors: string[] = [];

                if (isUserCrmSettingsChanged) {
                    const error = await saveUserCrmSettings(processedUserId, options);
                    if (error) {
                        updateUserErrors.push(error);
                    }
                }

                const errors = [...processUserErrors, ...updateUserErrors];
                if (errors.length) {
                    saveError = errors.join(' ');
                    setErrorMessage(saveError);
                } else {
                    proceed();
                }

                if (onUserCreate) {
                    onUserCreate(processedUserId);
                }
                setBusy(false);
            }, (error: string) => {
                saveError = error;
                setErrorMessage(error);
                setBusy(false);
            }, options);
            return saveError;
        } finally {
            setBusy(false);
        }
    }, [
        isUserCrmSettingsChanged,
        saveUserCrmSettings,
        userInformation,
        validationErrors,
        onUserCreate,
        proceed,
        setErrorMessage,
        processUser,
        validateUnfilledFields,
    ]);

    const onDiscard = useCallback(() => {
        discardChanges();
        discardUserCrmSetting();
        clearErrors();
    }, [discardChanges, discardUserCrmSetting, clearErrors]);

    const isLoading = isUserInformationLoading || isUserCrmSettingLoading;
    const loadingError = userInformationLoadingError || userCrmSettingsLoadingError;

    return children({
        isLoading,
        isChunkChanged: isCurrentChunkChanged,
        loadingError,
        busy,
        content: [
            <UserDetails
                disabled={editDisabled}
                userId={userId}
                isLoading={isLoading}
                key="UserDetailsSection"
                userDetails={userInformation}
                setUserDetails={setUserInformation}
                onSaveSfdcId={saveSfdcId}
                onFieldChange={onFieldChange('User Details')}
                onMoveToUserProfile={existedUserId => {
                    onDiscard();
                    proceed(existedUserId);
                }}
            />,
            <UserAccountInformation
                disabled={editDisabled}
                isLoading={isLoading}
                key="UserAccountInformationSection"
                availableStatuses={availableStatuses}
                userAccountInformation={userInformation}
                setUserAccountInformation={setUserInformation}
                userId={userId}
                accountId={accountId}
                onFieldChange={onFieldChange('User Account Information')}
            />,
            <UserCrmSettingsSection
                disabled={editDisabled}
                isLoading={isLoading}
                key="UserCrmSettingsSection"
                crmSettings={userCrmSettings}
                onCrmSettingsChange={setUserCrmSettings}
                userId={userId}
                onFieldChange={onFieldChange('CRM Settings')}
            />,
            <ContactInformation
                disabled={editDisabled}
                isLoading={isLoading}
                key="ContactInformationSection"
                userContactInfo={userInformation}
                setUserAccountInformation={setUserInformation}
                onFieldChange={onFieldChange('Contact Information')}
            />,
        ],
        onSave,
        onDiscard,
    });
};
